import React, { useState } from 'react';

// Utils
import { isNil } from 'lodash';
import { SortDirection } from 'ka-table/enums';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import useNotification from '../../../utils/hooks/useNotification';

// Services
import useAdministratorsService from '../../../services/administratorsService';
import useRolesService from '../../../services/rolesService';

// Components
import GenericTable from '../GenericTable/GenericTable';
import SkeletonLoader from '../../molecules/SkeletonLoader/SkeletonLoader';
import EmptyList from '../../organisms/EmptyList/EmptyList';
import TagWithEdition from '../../atoms/TagWithEdition/TagWithEdition';

function AdministrationTable() {
  const { t } = useTranslation();
  const { displayNotification } = useNotification();

  const { getAdministrators, postUpdateStatus } = useAdministratorsService();
  const { getRoles } = useRolesService();

  const [roles, setRoles] = useState(null);
  const [administrators, setAdministrators] = useState(null);

  const { mutate: statusMutate } = useMutation(
    ({ accountId, isActive }) => postUpdateStatus(accountId, isActive),
    {
      onMutate: (updated) => {
        setAdministrators((state) => state.map((admin) => {
          if (admin.id === updated.accountId) {
            return { ...admin, isActive: updated.isActive };
          }
          return admin;
        }));
      },
      onError: (_, updated) => {
        setAdministrators((state) => state.map((admin) => {
          if (admin.id === updated.accountId) {
            return { ...admin, isActive: !updated.isActive };
          }
          return admin;
        }));
      },
      onSuccess: (_, updated) => {
        const admin = administrators?.find((a) => a.id === updated.accountId);
        if (admin) {
          displayNotification({
            message: t('admin.table.actions.status.success', { user: `${admin.firstName} ${admin.lastName}` }),
          });
        }
      },
    },
  );

  const { isLoading: isLoadingRoles } = useQuery(['getRoles'], () => getRoles(), {
    onSuccess: (res) => setRoles(res?.data?.establishmentRoles || []),
  });

  const { isLoading } = useQuery(
    ['getAdministrators', roles],
    () => getAdministrators(),
    {
      enabled: !isNil(roles),
      onSuccess: (res) => setAdministrators(res?.data?.accounts || []),
    },
  );

  const columns = [
    {
      key: 'name', title: t('admin.table.columns.name'), sortDirection: SortDirection.Ascend, bold: true,
    },
    { key: 'email', title: t('admin.table.columns.email') },
    { key: 'role', title: t('admin.table.columns.role') },
    { key: 'status', title: t('admin.table.columns.status') },
    { key: 'actions', title: t('admin.table.columns.actions') },
  ];

  const getRow = (admin, index) => ({
    id: index,
    name: `${admin.lastName} ${admin.firstName}`,
    email: admin.emailAddress,
    role: roles?.find((r) => r.id === admin.roleId)?.name || t('common.undefined'),
    status: (
      <TagWithEdition
        theme={admin.isActive ? 'green' : 'yellow'}
        label={t(`tables.status.${admin.isActive ? 'active' : 'inactive'}`)}
        options={[
          { value: 'active', label: t('tables.status.active') },
          { value: 'inactive', label: t('tables.status.inactive') },
        ]}
        onChange={(status) => {
          statusMutate({ accountId: admin.id, isActive: status === 'active' });
        }}
        activeValue={admin.isActive ? 'active' : 'inactive'}
      />
    ),
    actions: <div>[ACTIONS]</div>,
  });

  return (
    (isLoading || isLoadingRoles) ? (
      <>
        <SkeletonLoader height={60} className="m-b-20" />
        <SkeletonLoader height={300} />
      </>
    ) : (
      administrators?.length ? (
        <GenericTable
          columns={columns}
          rows={administrators.map((admin, index) => getRow(admin, index))}
        />
      ) : (
        <EmptyList
          imageTheme="users"
          title={t('admin.table.empty.title')}
          text={t('admin.table.empty.text')}
          button={{
            label: t('admin.table.empty.button'),
            onClick: () => {},
          }}
        />
      )
    )
  );
}

export default AdministrationTable;
